/**
 * Reading and changing competencies, assignments and evidence.
 *
 * The catalog, the record and a person's own assignments come through the
 * callables. An administrator's view of everyone's assignments and evidence
 * for one competency is read from Firestore directly — the rules allow an
 * admin to read both collections, and there is no callable that lists them.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { collection, getDocs, getFirestore, query, where } from 'firebase/firestore';
import { app } from '../../lib/firebase';
import { call } from '../../lib/callable';
import { toIsoDateTime } from '../../shared/dates';
import {
  toAssignmentStatus, toStatus, NO_REQUIREMENTS,
  type Assignment, type CompetencyDetail, type CompetencyEvent, type CompetencyFields,
  type CompetencySummary, type CompetencyVersion, type Evidence, type EvidenceRequirements, toCompetencyType, toValidationMethods } from './types';

const str = (v: unknown): string | null => (typeof v === 'string' && v ? v : null);
const strs = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []);
const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);
/** A callable's ISO string, or a Firestore Timestamp read directly. */
const when = (v: unknown): string | null => {
  if (typeof v === 'string') return v || null;
  if (typeof v === 'object' && v !== null && 'toDate' in v) {
    const d = (v as { toDate: () => Date }).toDate();
    return Number.isNaN(d.getTime()) ? null : d.toISOString();
  }
  return null;
};

function toRequirements(v: unknown): EvidenceRequirements {
  const r = (typeof v === 'object' && v !== null ? v : {}) as Record<string, unknown>;
  return {
    attestation: r.attestation === true,
    fileUpload: r.fileUpload === true,
    managerApproval: r.managerApproval === true,
    attestationText: str(r.attestationText),
  };
}

function toSummary(d: Record<string, unknown>): CompetencySummary {
  return {
    id: String(d.id ?? ''),
    title: str(d.title) ?? '(untitled)',
    description: str(d.description),
    status: toStatus(d.status),
    appliesToRoles: strs(d.appliesToRoles),
    appliesToDepartments: strs(d.appliesToDepartments),
    renewalIntervalDays: num(d.renewalIntervalDays),
    competencyType: toCompetencyType(d.competencyType),
    validationMethods: toValidationMethods(d.validationMethods),
    currentVersionNumber: num(d.currentVersionNumber),
    updatedAt: when(d.updatedAt),
  };
}

export function toAssignment(d: Record<string, unknown>): Assignment {
  return {
    id: String(d.id ?? ''),
    competencyId: str(d.competencyId) ?? '',
    competencyVersionNumber: num(d.competencyVersionNumber) ?? 0,
    competencyTitle: str(d.competencyTitle) ?? '',
    userId: str(d.userId) ?? '',
    userEmail: str(d.userEmail),
    userDisplayName: str(d.userDisplayName),
    status: toAssignmentStatus(d.status),
    dueDate: when(d.dueDate),
    assignedAt: when(d.assignedAt),
    completedAt: when(d.completedAt),
    waivedAt: when(d.waivedAt),
    waivedReason: str(d.waivedReason),
    evidenceId: str(d.evidenceId),
  };
}

export function toEvidence(d: Record<string, unknown>): Evidence {
  const ma = typeof d.managerApproval === 'object' && d.managerApproval !== null ? (d.managerApproval as Record<string, unknown>) : null;
  return {
    id: String(d.id ?? ''),
    assignmentId: str(d.assignmentId) ?? '',
    competencyId: str(d.competencyId) ?? '',
    userId: str(d.userId) ?? '',
    userEmail: str(d.userEmail),
    userDisplayName: str(d.userDisplayName),
    submittedAt: when(d.submittedAt),
    attestationText: str(d.attestationText),
    fileUrls: strs(d.fileUrls),
    notes: str(d.notes),
    assessorName: str(d.assessorName),
    assessmentDate: str(d.assessmentDate),
    score: num(d.score),
    managerApproval: ma ? {
      approved: ma.approved === true, approvedByEmail: str(ma.approvedByEmail),
      approvedAt: when(ma.approvedAt), comments: str(ma.comments),
    } : null,
  };
}

export const competenciesKey = (orgId: string | null) => ['competencies', orgId] as const;
export const competencyKey = (orgId: string | null, id: string | null) => ['competency', orgId, id] as const;
export const myAssignmentsKey = (orgId: string | null, uid: string | null) => ['my-competency-assignments', orgId, uid] as const;
export const assignmentsKey = (orgId: string | null, id: string | null) => ['competency-assignments', orgId, id] as const;
export const evidenceKey = (orgId: string | null, id: string | null) => ['competency-evidence', orgId, id] as const;

export function useCompetencies(orgId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: competenciesKey(orgId),
    enabled: Boolean(orgId) && enabled,
    queryFn: async () => {
      const res = await call<Record<string, never>, { competencies: Record<string, unknown>[] }>('listCompetencies', {});
      return (res.competencies ?? []).map(toSummary);
    },
  });
}

export interface CompetencyDetailResult {
  competency: CompetencyDetail;
  versions: CompetencyVersion[];
  events: CompetencyEvent[];
}

export function useCompetencyDetail(orgId: string | null, id: string | null, enabled = true) {
  return useQuery({
    queryKey: competencyKey(orgId, id),
    enabled: Boolean(orgId && id) && enabled,
    queryFn: async (): Promise<CompetencyDetailResult> => {
      const res = await call<{ competencyId: string }, {
        competency: Record<string, unknown>; versions: Record<string, unknown>[]; events: Record<string, unknown>[];
      }>('getCompetencyDetail', { competencyId: id! });
      const c = res.competency ?? {};
      return {
        competency: {
          ...toSummary(c),
          evidenceRequirements: c.evidenceRequirements ? toRequirements(c.evidenceRequirements) : NO_REQUIREMENTS,
          currentVersionId: str(c.currentVersionId),
          createdAt: when(c.createdAt),
          publishedAt: when(c.publishedAt),
          retiredAt: when(c.retiredAt),
        },
        versions: (res.versions ?? []).map((v) => ({ id: String(v.id ?? ''), versionNumber: num(v.versionNumber) ?? 0, publishedAt: when(v.publishedAt) })),
        events: (res.events ?? []).map((e) => ({
          id: String(e.id ?? ''), action: str(e.action) ?? '', actorEmail: str(e.actorEmail),
          details: typeof e.details === 'object' && e.details !== null ? (e.details as Record<string, unknown>) : null,
          createdAt: when(e.createdAt),
        })),
      };
    },
  });
}

/** This person's own assignments, across every competency. */
export function useMyAssignments(orgId: string | null, uid: string | null, enabled: boolean) {
  return useQuery({
    queryKey: myAssignmentsKey(orgId, uid),
    enabled: Boolean(orgId && uid) && enabled,
    queryFn: async () => {
      const res = await call<Record<string, never>, { assignments: Record<string, unknown>[] }>('listMyCompetencyAssignments', {});
      return (res.assignments ?? []).map(toAssignment);
    },
  });
}

/** Everyone's assignments for one competency. Administrators only — the
 *  rules refuse the list to anyone else, so it is not even asked for. */
export function useCompetencyAssignments(orgId: string | null, competencyId: string | null, isAdmin: boolean) {
  return useQuery({
    queryKey: assignmentsKey(orgId, competencyId),
    enabled: Boolean(orgId && competencyId) && isAdmin,
    queryFn: async () => {
      const db = getFirestore(app);
      const snap = await getDocs(query(collection(db, 'orgs', orgId!, 'competencyAssignments'), where('competencyId', '==', competencyId)));
      return snap.docs.map((d) => toAssignment({ ...d.data(), id: d.id }));
    },
  });
}

export function useCompetencyEvidence(orgId: string | null, competencyId: string | null, isAdmin: boolean) {
  return useQuery({
    queryKey: evidenceKey(orgId, competencyId),
    enabled: Boolean(orgId && competencyId) && isAdmin,
    queryFn: async () => {
      const db = getFirestore(app);
      const snap = await getDocs(query(collection(db, 'orgs', orgId!, 'competencyEvidence'), where('competencyId', '==', competencyId)));
      return snap.docs.map((d) => toEvidence({ ...d.data(), id: d.id }));
    },
  });
}

export function toPayload(f: CompetencyFields): Record<string, unknown> {
  const days = f.renewalIntervalDays.trim() === '' ? undefined : Number(f.renewalIntervalDays);
  const req: Record<string, unknown> = {
    attestation: f.attestation, fileUpload: f.fileUpload, managerApproval: f.managerApproval,
  };
  if (f.attestation && f.attestationText.trim()) req.attestationText = f.attestationText.trim();
  const p: Record<string, unknown> = {
    title: f.title.trim(),
    description: f.description.trim(),
    appliesToRoles: f.appliesToRoles,
    appliesToDepartments: f.appliesToDepartments,
    evidenceRequirements: req,
    renewalIntervalDays: Number.isInteger(days) ? days : undefined,
    competencyType: f.competencyType,
    validationMethods: f.validationMethods,
  };
  for (const k of Object.keys(p)) if (p[k] === undefined) delete p[k];
  return p;
}

function useInvalidate(orgId: string | null, id: string | null) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: competenciesKey(orgId) });
    if (id) {
      void qc.invalidateQueries({ queryKey: competencyKey(orgId, id) });
      void qc.invalidateQueries({ queryKey: assignmentsKey(orgId, id) });
      void qc.invalidateQueries({ queryKey: evidenceKey(orgId, id) });
    }
    void qc.invalidateQueries({ queryKey: ['my-competency-assignments', orgId] });
  };
}

export function useCreateCompetency(orgId: string | null) {
  const done = useInvalidate(orgId, null);
  return useMutation({
    mutationFn: async (f: CompetencyFields) => (await call<unknown, { competencyId: string }>('createCompetency', toPayload(f))).competencyId,
    onSuccess: done,
  });
}

export function useUpdateCompetency(orgId: string | null, id: string) {
  const done = useInvalidate(orgId, id);
  return useMutation({
    mutationFn: async (f: CompetencyFields) => { await call<unknown, unknown>('updateCompetency', { competencyId: id, ...toPayload(f) }); },
    onSuccess: done,
  });
}

export type CompetencyAction =
  | { name: 'publishCompetency'; dueDaysDefault: string; dueDateOverride: string }
  | { name: 'retireCompetency'; reason: string }
  | { name: 'waiveCompetencyAssignment'; assignmentId: string; reason: string }
  | { name: 'approveEvidence'; evidenceId: string; approved: boolean; comments: string };

export interface PublishResult { versionNumber?: number; assignmentCount?: number; skipped?: boolean }

export function actionPayload(competencyId: string, a: CompetencyAction): Record<string, unknown> {
  switch (a.name) {
    case 'publishCompetency': {
      const p: Record<string, unknown> = { competencyId };
      const iso = toIsoDateTime(a.dueDateOverride);
      if (iso) p.dueDateOverride = iso;
      else if (a.dueDaysDefault.trim() && Number.isInteger(Number(a.dueDaysDefault))) p.dueDaysDefault = Number(a.dueDaysDefault);
      return p;
    }
    case 'retireCompetency': {
      const p: Record<string, unknown> = { competencyId };
      if (a.reason.trim()) p.reason = a.reason.trim();
      return p;
    }
    case 'waiveCompetencyAssignment':
      return { assignmentId: a.assignmentId, reason: a.reason.trim() };
    case 'approveEvidence': {
      const p: Record<string, unknown> = { evidenceId: a.evidenceId, approved: a.approved };
      if (a.comments.trim()) p.comments = a.comments.trim();
      return p;
    }
  }
}

export function useCompetencyAction(orgId: string | null, competencyId: string) {
  const done = useInvalidate(orgId, competencyId);
  return useMutation({
    mutationFn: async (a: CompetencyAction) => call<unknown, PublishResult>(a.name, actionPayload(competencyId, a)),
    onSuccess: done,
  });
}

export interface EvidenceInput {
  assignmentId: string;
  attestationText: string;
  fileUrls: string[];
  notes: string;
  assessorName?: string;
  /** "YYYY-MM-DD" or empty. */
  assessmentDate?: string;
  /** Digits or empty. */
  score?: string;
}

export function useSubmitEvidence(orgId: string | null, uid: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: EvidenceInput) => {
      const p: Record<string, unknown> = { assignmentId: input.assignmentId, fileUrls: input.fileUrls };
      if (input.attestationText.trim()) p.attestationText = input.attestationText.trim();
      if (input.notes.trim()) p.notes = input.notes.trim();
      if (input.assessorName?.trim()) p.assessorName = input.assessorName.trim();
      if (input.assessmentDate && /^\d{4}-\d{2}-\d{2}$/.test(input.assessmentDate)) p.assessmentDate = input.assessmentDate;
      if (input.score?.trim() && Number.isInteger(Number(input.score))) p.score = Number(input.score);
      return call<unknown, { success: boolean; evidenceId: string; needsManagerApproval: boolean }>('submitCompetencyEvidence', p);
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: myAssignmentsKey(orgId, uid) }),
  });
}
